import { Stack, useMantineTheme } from '@mantine/core';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { AdTagCreative as AdTagCreativeType } from '@feathr/blackbox';
import { CodeMirror, CopyToClipboardButton, Tooltip } from '@feathr/components';
import { DEFAULT_AD_TAG_HTML } from '@feathr/extender/components/creatives/display.utils';

import { AdDimensionsSelect } from '../../../AdDimensionsSelect';

interface IAdTagMacro {
  macro: string;
  tooltip: ReactNode;
}

interface IAdTagFieldsProps {
  creative: AdTagCreativeType;
}

function AdTagFields({ creative }: Readonly<IAdTagFieldsProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const adTagMacros: IAdTagMacro[] = [
    {
      macro: '@FEATHR_TRACK@',
      tooltip: (
        <Trans t={t}>
          This macro will be replaced with an HTML <code>img</code> tag for tracking impressions.
        </Trans>
      ),
    },
    {
      macro: '@FEATHR_CLICK@',
      tooltip: t('This macro will be replaced with a click-through URL for tracking clicks.'),
    },
    {
      macro: '@FEATHR_CLICK_ESC@',
      tooltip: t(
        'This macro will be replaced with a URI-encoded version of the click-through URL for tracking clicks.',
      ),
    },
  ];

  if (creative.get('adtag') === undefined) {
    runInAction(() => {
      creative.set({ adtag: DEFAULT_AD_TAG_HTML });
    });
  }

  return (
    <>
      <CodeMirror
        attribute={'adtag'}
        helpText={t('Include all necessary script tags, iframes, and tracking pixels')}
        label={t('Ad Tag HTML')}
        model={creative}
        name={'adtag'}
        options={{ height: 200 }}
      />
      <Stack gap={theme.spacing['3']}>
        {adTagMacros.map(({ macro, tooltip }) => (
          <Tooltip key={macro} title={tooltip}>
            <CopyToClipboardButton t={t} text={macro}>
              <code>{macro}</code>
            </CopyToClipboardButton>
          </Tooltip>
        ))}
      </Stack>
      <AdDimensionsSelect
        channel={'display'}
        creative={creative}
        helpText={t('Select the dimensions that match your ad tag creative')}
        label={t('Dimensions')}
      />
    </>
  );
}

export default observer(AdTagFields);
